import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { loadElk, computeLayout } from 'src/lib/workflows/autoLayout';
import {
  buildStepLayoutGroupGraph,
  extractStepLayoutGroups,
  refitStepLayoutGroupsAroundMembers,
  toAbsoluteStepNodes,
} from 'src/lib/workflows/stepLayoutGroups';

export function useAutoLayout() {
  const { data: elk } = useQuery({
    queryKey: ['elk-instance'],
    queryFn: loadElk,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const getAutoLayoutedNodes = useCallback(
    async <T extends Node>(nodes: T[], edges: Edge[]): Promise<T[]> => {
      if (!elk) return nodes;
      try {
        // Step layout groups are flattened out before layout and rebuilt after: ELK
        // sees a plain absolute-coordinate DAG, which is all it can lay out
        // sensibly, and each group then shrink-wraps its members again.
        // Centralised here so none of the call sites can hand ELK a container
        // node and get a group laid out as though it were a step.
        const stepLayoutGroups = extractStepLayoutGroups(nodes);
        if (stepLayoutGroups.length === 0) {
          return await computeLayout(elk, nodes, edges);
        }

        const laidOutSteps = await computeLayout(
          elk,
          toAbsoluteStepNodes(nodes),
          edges,
        );
        return buildStepLayoutGroupGraph(
          laidOutSteps,
          refitStepLayoutGroupsAroundMembers(stepLayoutGroups, laidOutSteps),
        ) as T[];
      } catch {
        return nodes;
      }
    },
    [elk],
  );

  return { getAutoLayoutedNodes, isElkReady: !!elk };
}
